import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, Navigate, NavLink, useNavigate, useParams } from 'react-router-dom'
import type { EpisodeSummary, Recording, Show, Workspace } from '../api/types'
import { FileDrop } from '../components/FileDrop'
import { LiveCard } from '../components/LiveCard'
import { ListenersCard } from '../components/ListenersCard'
import { Modal } from '../components/Modal'
import { PublishedBadge } from '../components/PublishCard'
import { ShowForm } from '../components/ShowForm'
import { ShowPublishing } from '../components/ShowPublishing'
import { EpisodeStatusBadge } from '../components/StatusBadges'
import { Badge, Button, Card, EmptyState, ErrorNote, Field, LoadingBlock, PageHeader, ProgressBar } from '../components/ui'
import { refreshEpisode, useCreateEpisode, useDeleteShow, useEpisodes, useImportSourceUrl, useRecordings, useShow } from '../hooks/useShows'
import { useShowStats } from '../hooks/useStats'
import { useCurrentWorkspace } from '../hooks/useWorkspace'
import { isActive, startUpload, useUpload } from '../uploads/store'
import { formatBytes, formatDuration, timeAgo, titleFromFilename } from '../utils/format'

/** The show's subpages, by URL segment; episodes is the bare show URL. */
const SECTIONS = ['episodes', 'live', 'feed', 'recordings'] as const
type Section = (typeof SECTIONS)[number]

export function ShowPage() {
  const { showId = '', section = 'episodes' } = useParams()
  const { workspace } = useCurrentWorkspace()
  if (!workspace) return <Navigate to="/studio" replace />
  if (!SECTIONS.includes(section as Section)) return <Navigate to={`/studio/shows/${showId}`} replace />
  return <ShowView workspace={workspace} showId={showId} section={section as Section} />
}

function ShowView({ workspace, showId, section }: { workspace: Workspace; showId: string; section: Section }) {
  const ws = workspace.id
  const show = useShow(ws, showId)
  const episodes = useEpisodes(ws, showId)
  const stats = useShowStats(ws, showId)
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(false)
  const canEdit = workspace.role !== 'viewer'

  if (show.isPending) return <LoadingBlock />
  if (show.isError) return <ErrorNote error={show.error} />
  return (
    <>
      <div className="breadcrumbs"><Link to="/studio">Shows</Link></div>
      <PageHeader
        title={show.data.title}
        subtitle={
          <span className="row">
            <Badge>{show.data.medium}</Badge>
            {show.data.description && <span className="muted">{show.data.description}</span>}
          </span>
        }
        actions={
          canEdit && (
            <>
              <Button variant="secondary" onClick={() => setEditing(true)}>Edit show</Button>
              <Button onClick={() => setAdding(true)}>New episode</Button>
            </>
          )
        }
      />
      <nav className="pills" aria-label="Show sections">
        <NavLink to={`/studio/shows/${showId}`} end>Episodes</NavLink>
        <NavLink to={`/studio/shows/${showId}/live`}>Live</NavLink>
        <NavLink to={`/studio/shows/${showId}/feed`}>Podcast feed</NavLink>
        <NavLink to={`/studio/shows/${showId}/recordings`}>Recordings</NavLink>
      </nav>
      {section === 'live' && <LiveCard workspaceId={ws} show={show.data} canEdit={canEdit} />}
      {section === 'feed' && (
        <div className="stack-lg">
          <ShowPublishing workspaceId={ws} show={show.data} canEdit={canEdit} />
          {show.data.published_episodes > 0 && <ListenersCard query={stats} />}
        </div>
      )}
      {section === 'recordings' && <RecordingsCard workspaceId={ws} showId={showId} />}
      {section === 'episodes' && (
        <Card title="Episodes">
          {episodes.isPending && <LoadingBlock />}
          {episodes.isError && <ErrorNote error={episodes.error} />}
          {episodes.data?.length === 0 && (
            <EmptyState
              title="No episodes yet"
              body="Upload a recording to create the first one."
              action={canEdit && <Button onClick={() => setAdding(true)}>New episode</Button>}
            />
          )}
          {episodes.data && episodes.data.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Episode</th><th>Status</th><th>Format</th><th>Length</th><th>Created</th></tr>
                </thead>
                <tbody>
                  {episodes.data.map((episode) => (
                    <EpisodeRow key={episode.id} episode={episode} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      <Modal open={adding} title="New episode" onClose={() => setAdding(false)} wide>
        <NewEpisodeForm
          workspaceId={ws}
          show={show.data}
          onCreated={(episode) => {
            setAdding(false)
            navigate(`/studio/episodes/${episode.id}`)
          }}
        />
      </Modal>
      <Modal open={editing} title="Edit show" onClose={() => setEditing(false)} wide>
        <ShowForm workspaceId={ws} show={show.data} onDone={() => setEditing(false)} />
        {workspace.role === 'owner' && <DeleteShow workspaceId={ws} show={show.data} onDeleted={() => navigate('/studio')} />}
      </Modal>
    </>
  )
}

/** Every original recording, to keep a copy of. Links save the file rather than play it. */
function RecordingsCard({ workspaceId, showId }: { workspaceId: string; showId: string }) {
  const recordings = useRecordings(workspaceId, showId)
  const total = recordings.data?.reduce((sum, r) => sum + (r.bytes ?? 0), 0) ?? 0
  return (
    <Card title="Recordings" actions={recordings.data && recordings.data.length > 0 ? <span className="muted small">{recordings.data.length} files · {formatBytes(total)}</span> : undefined}>
      <div className="stack">
        <p className="muted small">
          The original file of every episode, as you uploaded it or as your live broadcast was recorded. Download them to keep your own backup;
          each link saves the file under the episode's name.
        </p>
        {recordings.isPending && <LoadingBlock />}
        {recordings.isError && <ErrorNote error={recordings.error} />}
        {recordings.data?.length === 0 && <EmptyState title="No recordings yet" body="Recordings appear here once an episode's file has been uploaded." />}
        {recordings.data && recordings.data.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Episode</th><th>Format</th><th>Length</th><th>Size</th><th>Uploaded</th><th></th></tr>
              </thead>
              <tbody>
                {recordings.data.map((recording) => (
                  <RecordingRow key={recording.episode_id} recording={recording} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

function RecordingRow({ recording }: { recording: Recording }) {
  return (
    <tr>
      <td>
        <Link to={`/studio/episodes/${recording.episode_id}`}>{recording.title}</Link>
        {recording.original_filename && <div className="muted small">{recording.original_filename}</div>}
      </td>
      <td>{recording.medium ? <Badge>{recording.medium}</Badge> : <span className="muted">—</span>}</td>
      <td>{formatDuration(recording.duration_ms)}</td>
      <td>{formatBytes(recording.bytes)}</td>
      <td className="muted">{timeAgo(recording.created_at)}</td>
      <td className="num">
        <a href={recording.download_url} download>Download</a>
      </td>
    </tr>
  )
}

function EpisodeRow({ episode }: { episode: EpisodeSummary }) {
  const upload = useUpload(episode.id)
  const uploading = isActive(upload) && upload
  return (
    <tr>
      <td><Link to={`/studio/episodes/${episode.id}`}>{episode.title}</Link></td>
      <td>
        {uploading ? (
          <div className="row nowrap">
            <ProgressBar value={uploading.size ? (uploading.sent / uploading.size) * 100 : 0} label="Upload progress" />
            <span className="muted small">{Math.floor(uploading.size ? (uploading.sent / uploading.size) * 100 : 0)}%</span>
          </div>
        ) : (
          <span className="row"><EpisodeStatusBadge status={episode.status} /><PublishedBadge publishedAt={episode.published_at} /></span>
        )}
      </td>
      <td className="muted">{episode.medium ?? '—'}</td>
      <td className="muted">{formatDuration(episode.duration_ms)}</td>
      <td className="muted">{timeAgo(episode.created_at)}</td>
    </tr>
  )
}

function NewEpisodeForm({ workspaceId, show, onCreated }: { workspaceId: string; show: Show; onCreated: (episode: EpisodeSummary) => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [link, setLink] = useState('')
  const [title, setTitle] = useState('')
  // Kept when the link is refused, so trying again does not make a second episode.
  const [made, setMade] = useState<EpisodeSummary | null>(null)
  const create = useCreateEpisode(workspaceId, show.id)
  const importLink = useImportSourceUrl(workspaceId)
  const queryClient = useQueryClient()
  const url = link.trim()
  return (
    <form
      className="stack"
      onSubmit={async (event) => {
        event.preventDefault()
        const name = title.trim() || (file ? titleFromFilename(file.name) : url ? titleFromFilename((url.split(/[?#]/)[0] ?? '').split('/').pop() ?? '') : '')
        const episode = made ?? (await create.mutateAsync({ title: name }).catch(() => null))
        if (!episode) return
        setMade(episode)
        if (file) {
          void startUpload(workspaceId, episode.id, file, () => refreshEpisode(queryClient, workspaceId, episode.id))
        } else if (url && !(await importLink.mutateAsync({ episodeId: episode.id, url }).then(() => true, () => false))) {
          return
        }
        onCreated(episode)
      }}
    >
      <FileDrop
        file={file}
        onFile={(chosen) => {
          setFile(chosen)
          if (!title.trim()) setTitle(titleFromFilename(chosen.name))
        }}
      />
      {!file && (
        <Field label="Or a link to the recording" hint="An audio or video file that is already online. indiefuture copies it, then processes it like an upload.">
          <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://example.com/episode-1.mp3" />
        </Field>
      )}
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} placeholder="Episode 1: Why we started" />
      </Field>
      {create.error && <ErrorNote error={create.error} />}
      {importLink.error && <ErrorNote error={importLink.error} />}
      <div className="row">
        <Button type="submit" busy={create.isPending || importLink.isPending} disabled={!file && !url && !title.trim()}>
          {file ? 'Create and upload' : url ? 'Create and copy' : 'Create episode'}
        </Button>
        <span className="muted small">The upload keeps going while you move around the app.</span>
      </div>
    </form>
  )
}

function DeleteShow({ workspaceId, show, onDeleted }: { workspaceId: string; show: Show; onDeleted: () => void }) {
  const remove = useDeleteShow(workspaceId, show.id)
  return (
    <div className="danger-zone stack-sm">
      <strong>Delete this show</strong>
      <span className="muted small">Deletes its {show.episode_count} episodes and every stored file. This cannot be undone.</span>
      {remove.error && <ErrorNote error={remove.error} />}
      <div>
        <Button
          variant="danger"
          busy={remove.isPending}
          onClick={() => {
            if (window.confirm(`Delete "${show.title}" and all of its episodes?`)) remove.mutate(undefined, { onSuccess: onDeleted })
          }}
        >
          Delete show
        </Button>
      </div>
    </div>
  )
}
